<template>
  <div class='referral-code-modal'>
    <ArrowButton @click="showModal" text="Get your referral code" />
    <b-modal 
      ref="my-modal"
      modal-class="custom-modal" 
      title="Get your referral link"
      @show="onShow"
      @hidden="onHide"
      centered
      hide-header
      hide-footer 
      >
      <div class="modal-inner">
        <h4 class="h4 mb-3">Get your referral link</h4>
        <div v-if="state === 'address'">
          <p class='mb-3' >Please enter the address where you currently have AT&T Fiber service or receive your AT&T Wireless bill.</p>
          <div class="alert alert-danger" v-if="failed">
            Sorry, we're not able to process your request at this time. Please try again later.
          </div>
          <AddressInput @update="address=$event" ref="address">
            <template slot="validation">
              <div v-if="showValidationError" class="d-block invalid-feedback font-weight-bold">
                Enter your street address in this format 123 Main St Bothell, WA 98011
              </div>
            </template>
          </AddressInput>
          <div class="d-flex justify-content-end mt-5">
            <b-button @click="hideModal" variant="secondary">Cancel</b-button>
            <ArrowButton :busy="busy" :disabled="!validAddress" @click="checkEligibilityHandler" text="Search" />
          </div>
        </div>
        <div v-if="state === 'mdu'">
          <div v-if="maxMduOptions">
            <p>Opps, it appears this address has multiple units. Please provide more information:</p>
            <b-form-group label="Second address line">
              <select class="form-control custom-select" name="unit" v-model="unitType">
                <option :value="null">Please Select</option>
                <option value="APT">Apartment</option>
                <option value="BLDG">Building</option>
                <option value="FLR">Floor</option>
                <option value="LOT">Lot</option>
                <option value="RM">Room</option>
                <option value="SLIP">Slip</option>
              </select>
            </b-form-group>
            <b-form-group label="Unit type number">
              <b-form-input
                class="mt-1"
                maxlength="12"
                placeholder="Example: 4B"
                trim
                v-model="unitNumber"
              ></b-form-input>
            </b-form-group>
          </div>
          <div v-else>
            <p>Opps, it appears this address has multiple units. Please select your unit from the following options:</p>
            <b-form-group
              label="ADDRESS UNIT:"
            >
              <select class="form-control custom-select" name="unit" v-model="selectedMdu">
                <option :value="null">Please Select</option>
                <option :value="option.value" v-for="(option, i) in formattedMduOptions" :key="i">{{option.text}}</option>
              </select>
            </b-form-group>
          </div>
          <div class="d-flex justify-content-end mt-5">
            <b-button @click="hideModal" variant="secondary">Cancel</b-button>
            <ArrowButton :busy="busy" :disabled="invalidMdu" @click="checkMduEligibilityHandler" text="Continue" />
          </div>
        </div>
        <div v-if="state === 'closematch'">
          <p>We couldn't pinpoint your address, but we did find one or more close matches. Please select your address from the dropdown menu.</p>
          <b-form-group
            label="MATCHING ADDRESSES:"
          >
            <select class="form-control custom-select" name="unit" v-model="selectedCloseMatch">
              <option :value="null">Please Select</option>
              <option :value="option.value" v-for="(option, i) in formattedCloseMatchOptions" :key="i">{{option.text}}</option>
            </select>
          </b-form-group>
          <div class="d-flex justify-content-end mt-5">
            <b-button @click="hideModal" variant="secondary">Cancel</b-button>
            <ArrowButton :busy="busy" :disabled="invalidCloseMatch" @click="checkCloseMatchEligibilityHandler" text="Continue" />
          </div>
        </div>
        <div v-if="state === 'nomatch'">
          <p class="font-weight-bold">We're sorry. We can't find your address.</p>
          <p>{{address}}</p>
          <p><b-button variant="link" @click="resetForm">Try Again</b-button></p>
          <div class="d-flex justify-content-end mt-5">
            <b-button @click="hideModal" variant="secondary">Cancel</b-button>
          </div>
        </div>
        <div v-if="state === 'additional'">
          <p>Thank you!</p>
          <p>Please share some additional details with us. We will use this information to ensure that referrals 
            are credited to the right place or contact you in case of questions related to referrals or credits 
            to your account.</p>
          <div class="alert alert-danger" v-if="failed">
            Sorry, we're not able to process your request at this time. Please try again later.
          </div>

          <b-form>
            <b-form-group
              id="input-group-phone"
              class="mt-3 form-group-required"
              label="PHONE NUMBER"
              label-for="phone"
            >
              <b-form-input
                id="phone"
                ref="phone"
                type="tel"
                class="mt-1"
                v-mask="'###-###-####'"
                maxlength="12"
                trim
                placeholder="555-555-5555"
                v-model="userStore.phone"
                required
                aria-describedby="input-phone-feedback"
                :state="validationState('phone')"
              ></b-form-input>
              <b-form-invalid-feedback id="input-phone-feedback" class="font-weight-bold" :state="validationState('phone')">
                {{validationMessages('phone')}}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              id="input-group-email"
              class="mt-3 form-group-required"
              label="EMAIL ADDRESS"
              label-for="email"
            >
              <b-form-input
                id="email"
                type="email"
                class="mt-1"
                maxlength="256"
                trim                
                placeholder="email@domain.com"
                v-model="userStore.email"
                :state="validationState('email')"
              ></b-form-input>
              <b-form-invalid-feedback id="input-phone-feedback" class="font-weight-bold" :state="validationState('email')">
                {{validationMessages('email')}}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              id="input-group-ban"
              class="mt-3 group-optional form-group-required"
              description="Referral credits will be awarded to this account"
              label="AT&T ACCOUNT NUMBER"
              label-for="ban"
            >
              <b-form-select 
                v-model="userStore.banType" 
                :options="[{text:'Account Type...',value:''},{value:'Wireless',text:'Wireless'},{value:'Fiber',text:'Fiber'}]"
                :state="validationState('banType')"
              >
              </b-form-select>
              <b-form-invalid-feedback id="input-ban-feedback" class="font-weight-bold" :state="validationState('banType')">
                {{validationMessages('banType')}}
              </b-form-invalid-feedback>
              <b-form-input
                id="ban"
                type="text"
                class="mt-1"
                trim
                placeholder="Billing Account Number"
                v-model="userStore.ban"
                maxlength="15"
                :required="true"
                :state="validationState('ban')"
              ></b-form-input>
              <b-form-invalid-feedback id="input-ban-feedback" class="font-weight-bold" :state="validationState('ban')">
                {{validationMessages('ban')}}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group class="mt-3">
              <b-form-checkbox
                id="checkbox-1"
                size="lg"                
                v-model="agree"
                name="checkbox-1"
              >
                I understand that this offer is only available to existing AT&T wireless or fiber customers
              </b-form-checkbox>
            </b-form-group>
            <div class="d-flex justify-content-end mt-5">
              <b-button @click="hideModal" variant="secondary">Cancel</b-button>
              <ArrowButton :busy="busy" :disabled="!validForm" @click="createReferralHandler" text="Create Referral" />
            </div>
          </b-form>
        </div>
        <div v-if="state === 'ineligible'">
          <p class='mb-2'>Sorry! Based on the information you provided it doesn’t look like you’re eligible for the AT&T Refer-A-Friend program.</p>
          <p class='mb-2'>If you think this is incorrect, try re-entering the complete address where you have AT&T service. If applicable, please ensure you include unit/apartment number.</p>
          <p><b-button variant="link" @click="resetForm">Try Again</b-button></p>
          <div class="d-flex justify-content-end mt-5">
            <ArrowButton @click="hideModal" text="Close" />
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { mapStores, mapActions } from 'pinia'
  import parser from 'parse-address'
  import axios from 'axios'
  import validationMixin from '@/mixins/validation'

  import AddressInput from '@/components/AddressInput'
  import ArrowButton from '@/components/ArrowButton'
  import useUserStore from '@/stores/user'
  import VueTypeaheadBootstrap from '@nathan-cv/vue-typeahead-bootstrap'

  const SMARTY_KEY = window.SMARTY_KEY

  export default {
    components: {
      ArrowButton,
      AddressInput,
      VueTypeaheadBootstrap,
    },
    mixins: [
      validationMixin,
    ],
    computed: {
      ...mapStores(useUserStore),    
      validForm() {
        return this.agree
      },
      invalidMdu() {
        return this.maxMduOptions
          ? (this.unitType == null || this.unitNumber == null)
          : this.selectedMdu === null
      },
      invalidCloseMatch() {
        return this.selectedCloseMatch === null
      },
      maxMduOptions() {
        return this.mduOptions.length === 25
      },
      formattedMduOptions() {
        return this.mduOptions.map(option => {
          return {
            value: option,
            text: option.addressLine2,
          }
        })
      },
      formattedCloseMatchOptions() {
        return this.closeMatchOptions.map(option => {
          return {
            value: option,
            text: option.addressLine1,
          }
        })
      },
      showValidationError() {
        if (this.parsedAddress === null) return false

        let props = 0
        
        if (this.parsedAddress.street_line !== undefined) props++
        if (this.parsedAddress.city !== undefined) props++
        if (this.parsedAddress.state !== undefined) props++
        if (this.parsedAddress.zipcode !== undefined) props++
      
        return props > 2 && this.validAddress === false
      },
      parsedAddress() {
        if (this.address === '') return null

        const parsed = parser.parseLocation(this.address)

        const parsedStreet = [
          parsed.number, 
          this.address.includes("1/2") ? "1/2" : void 0, 
          parsed.prefix, 
          parsed.street, 
          parsed.type, 
          parsed.suffix
        ].filter(i => i).join(" ")
  
        return {
          parsed: true,
          street_line: parsedStreet,
          city: parsed.city,
          state: parsed.state,
          unitType: parsed.sec_unit_type,
          unitNumber: parsed.sec_unit_num,
          zipcode: parsed.zip,
        }
      },
      validAddress() {
        if (this.userStore.isValidAddress) return true
        if (this.parsedAddress === null) return false
        return this.parsedAddress.street_line !== undefined
          && this.parsedAddress.city !== undefined
          && this.parsedAddress.state !== undefined
          && this.parsedAddress.zipcode !== undefined
      }    
    },
    data () {
      return {
        state: 'address',
        busy: false,
        failed: false,
        agree: false,
        address: '',
        unitType: null,
        unitNumber: null,
      }
    },
    methods: {
      ...mapActions(useUserStore, ['resetAddress', 'createReferral', 'checkEligibility']),
      async createReferralHandler() {
        if (!this.userStore.phone) {
          this.addErrors({phone: ['Phone number is required.']})
          return
        }
        if (!this.userStore.email) {
          this.addErrors({email: ['Email address is required.']})
          return
        }
        try {
          this.busy = true
          this.failed = false
          this.clearValidation()
          const success = await this.createReferral()  
          this.failed = !success
          this.busy = false
          window.scrollTo(0, 0)
        } catch (err) {
          this.busy = false
          this.failed = false
          this.handleErrors(err)
          console.log(err)
        }
      },
      onShow () {
        window.scrollTo(0,0)
        window.setTimeout(() => {
           const [ input ] = document.getElementsByName('address')
           if (input) {
            input.focus()
           }
        }, 300)
      },
      onHide () {
        this.busy = false
        this.loading = false
        this.failed = false
        this.state = 'address'
        this.resetForm()
        this.clearValidation()
        this.$emit('close')
        window.scrollTo(0,0)
      },
      showModal() {
        this.$refs['my-modal'].show()
      },
      hideModal() {
        this.$refs['my-modal'].hide()
      },
      resetForm () {
        this.busy = false
        this.loading = false
        this.failed = false
        this.state = 'address'
        this.clearValidation()
        this.resetAddress()
      },
      async checkEligibilityHandler() {
        this.failed = false
        this.busy = true
        try {
          if (!this.userStore.currentAddress) {
            const addressRoute = this.userStore.isProd
              ? '/smartystreets/street-address'
              : `${this.userStore.apiRoot}/api/street-address`

            const payload = {
              'auth-id': SMARTY_KEY,
              street: this.address
            }

            const resp = await axios.get(addressRoute, { params: payload })

            const [verifiedAddress] = resp.data

            const currentAddress = verifiedAddress
              ? 
                {
                  street_line: verifiedAddress.delivery_line_1,
                  city: verifiedAddress.components.city_name,
                  state: verifiedAddress.components.state_abbreviation,
                  zipcode: verifiedAddress.components.zipcode,
                }
              : this.parsedAddress

            this.userStore.$patch({ currentAddress })
          }
          
          this.state = 'additional'
          window.setTimeout(() => {
            document.getElementById('phone').focus()
          }, 400)
          this.busy = false
        } catch {
          this.failed = true
          this.busy = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.modal-inner {
  color: #000;
  padding: 30px;
  p {
    @include font-light;
  }
  .btn-link {
    border: 0;
    box-shadow: none;
    padding: 0;
    text-align: left;
    height: unset;
  }
  ::v-deep {
    label {
      @include font-medium;
      font-size: 16px;
      text-transform: uppercase;
      margin-bottom:  5px;
    }
    select {
      width: 100%;
      border-radius: 0;
      height: 43px;
      font-size: 16px;
    }
  }
}

.custom-select > option {
  font-style: italic !important;
}
</style>